import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import * as XLSX from 'xlsx'
import { C, R, F, btnPrimary, btnSecondary, btnDanger, btnGhost, card, input, thStyle, pageHeader, statusBadge, spinner } from '../lib/designTokens'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const YEARS  = Array.from({length:10},(_,i)=>2020+i)
const fmt = n => new Intl.NumberFormat('en-US',{maximumFractionDigits:0}).format(n||0)
const periodLabel = e => e.period_type==='yearly'?`${e.year}`:`${MONTHS[(e.month||1)-1]} ${e.year}`

const EMPTY_QUICK  = { sub_code_id:'', total_salary:'', period_type:'monthly', year:new Date().getFullYear(), month:new Date().getMonth()+1, notes:'', status:'draft', cost_type:'direct' }
const EMPTY_DETAIL = { sub_code_id:'', employee_name:'', job_title:'', employee_type:'monthly', period_type:'monthly', year:new Date().getFullYear(), month:new Date().getMonth()+1, days_worked:'', basic_salary:'', housing_allowance:'', transport_allowance:'', food_allowance:'', other_allowance:'', overtime:'', deductions:'', notes:'', status:'draft', cost_type:'direct' }

function calcTotal(f) {
  const basic=parseFloat(f.basic_salary)||0; const days=parseFloat(f.days_worked)||0
  const dailyBasic=f.employee_type==='daily'?basic*days:basic
  return dailyBasic+(parseFloat(f.housing_allowance)||0)+(parseFloat(f.transport_allowance)||0)+(parseFloat(f.food_allowance)||0)+(parseFloat(f.other_allowance)||0)+(parseFloat(f.overtime)||0)-(parseFloat(f.deductions)||0)
}

const Toast = ({message,type,onClose}) => (
  <div style={{position:'fixed',bottom:24,right:24,zIndex:999,display:'flex',alignItems:'center',gap:10,padding:'11px 18px',borderRadius:R.card,boxShadow:'0 4px 20px rgba(0,0,0,.18)',fontSize:F.body,fontWeight:600,background:type==='success'?C.green:C.red,color:'#fff',minWidth:240}}>
    <span style={{flex:1}}>{message}</span>
    <button onClick={onClose} style={{background:'none',border:'none',color:'rgba(255,255,255,.7)',cursor:'pointer',fontSize:16}}>×</button>
  </div>
)

const StatusPill = ({status}) => {
  const s = statusBadge(status)
  return <span style={{fontSize:F.caption,fontWeight:600,padding:'2px 8px',borderRadius:R.badge,background:s.bg,color:s.color,border:`1px solid ${s.border}`}}>{status}</span>
}

const Field = ({label,children}) => (
  <div>
    <div style={{fontSize:F.caption,fontWeight:600,color:C.sub,marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>{label}</div>
    {children}
  </div>
)

export default function Payroll({ projectId: initProjectId }) {
  const [projectId,       setProjectId]       = useState(initProjectId||null)
  const [projects,        setProjects]        = useState([])
  const [projectName,     setProjectName]     = useState('')
  const [costCodes,       setCostCodes]       = useState([])
  const [subCodesList,    setSubCodesList]    = useState([]) // level=2 with resolved Cost Code + Resource Code ancestry
  const [activeTab,       setActiveTab]       = useState('quick')
  const [toast,           setToast]           = useState(null)
  const [quickEntries,    setQuickEntries]    = useState([])
  const [quickLoading,    setQuickLoading]    = useState(false)
  const [showQuickModal,  setShowQuickModal]  = useState(false)
  const [editQuick,       setEditQuick]       = useState(null)
  const [quickForm,       setQuickForm]       = useState(EMPTY_QUICK)
  const [detailEntries,   setDetailEntries]   = useState([])
  const [detailLoading,   setDetailLoading]   = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [editDetail,      setEditDetail]      = useState(null)
  const [detailForm,      setDetailForm]      = useState(EMPTY_DETAIL)
  const [showQuickUpload,  setShowQuickUpload]  = useState(false)
  const [quickUploadRows,  setQuickUploadRows]  = useState([])
  const [showDetailUpload, setShowDetailUpload] = useState(false)
  const [detailUploadRows, setDetailUploadRows] = useState([])
  const [selectedQuick,   setSelectedQuick]   = useState(new Set())
  const [selectedDetail,  setSelectedDetail]  = useState(new Set())
  const quickFileRef = useRef(); const detailFileRef = useRef()
  const quickUploadRef = useRef([]); const detailUploadRef = useRef([])

  const showToast = (msg,type='success') => { setToast({message:msg,type}); setTimeout(()=>setToast(null),4000) }

  useEffect(()=>{ if(initProjectId) setProjectId(initProjectId) },[initProjectId])
  useEffect(()=>{ fetchProjects() },[])
  useEffect(()=>{ if(projectId){ fetchCostCodes(); fetchQuick(); fetchDetail() } },[projectId])

  async function fetchProjects() { const {data}=await supabase.from('projects').select('id,name').order('name'); setProjects(data||[]) }
  async function fetchCostCodes() {
    const {data}=await supabase.from('cost_codes').select('id,code,description,level,parent_id').eq('project_id',projectId).eq('is_active',true).order('code')
    const all = data||[]
    setCostCodes(all)
    const byId = {}
    all.forEach(c => { byId[c.id] = c })
    const subs = all.filter(c => parseInt(c.level) === 2).map(sub => {
      const parent = byId[sub.parent_id]
      let costCode = null, resourceCode = null
      if (parent && parseInt(parent.level) === 4) {
        resourceCode = parent
        costCode = byId[parent.parent_id] || null
      } else if (parent && parseInt(parent.level) === 1) {
        costCode = parent
      }
      return {
        ...sub,
        costCode: costCode?.code || '—',
        costCodeDescription: costCode?.description || '',
        resourceCode: resourceCode?.code || null,
        resourceCodeDescription: resourceCode?.description || null,
      }
    })
    setSubCodesList(subs)
  }
  async function fetchQuick() { setQuickLoading(true); const {data}=await supabase.from('payroll_entries').select('*,cost_codes:sub_code_id(code,description)').eq('project_id',projectId).eq('entry_type','quick').order('year',{ascending:false}).order('month',{ascending:false}); setQuickEntries(data||[]); setQuickLoading(false) }
  async function fetchDetail() { setDetailLoading(true); const {data}=await supabase.from('payroll_entries').select('*,cost_codes:sub_code_id(code,description)').eq('project_id',projectId).eq('entry_type','detailed').order('year',{ascending:false}).order('month',{ascending:false}); setDetailEntries(data||[]); setDetailLoading(false) }

  async function saveQuick() {
    if(!quickForm.sub_code_id||!quickForm.total_salary) return showToast('Sub Code & Total Salary required','error')
    const payload={project_id:projectId,entry_type:'quick',sub_code_id:quickForm.sub_code_id,total_salary:parseFloat(quickForm.total_salary)||0,period_type:quickForm.period_type,year:parseInt(quickForm.year),month:quickForm.period_type==='yearly'?null:parseInt(quickForm.month),notes:quickForm.notes,status:quickForm.status,cost_type:quickForm.cost_type}
    const {error}=editQuick?await supabase.from('payroll_entries').update(payload).eq('id',editQuick.id):await supabase.from('payroll_entries').insert(payload)
    if(error) return showToast(error.message,'error')
    setShowQuickModal(false); showToast(editQuick?'Updated':'Added'); fetchQuick()
  }
  async function saveDetail() {
    if(!detailForm.sub_code_id||!detailForm.employee_name) return showToast('Sub Code & Employee Name required','error')
    const total=calcTotal(detailForm)
    const totalAllowances=(parseFloat(detailForm.housing_allowance)||0)+(parseFloat(detailForm.transport_allowance)||0)+(parseFloat(detailForm.food_allowance)||0)+(parseFloat(detailForm.other_allowance)||0)
    const payload={project_id:projectId,entry_type:'detailed',sub_code_id:detailForm.sub_code_id,employee_name:detailForm.employee_name,job_title:detailForm.job_title,employee_type:detailForm.employee_type,period_type:detailForm.period_type,year:parseInt(detailForm.year),month:detailForm.period_type==='yearly'?null:parseInt(detailForm.month),days_worked:parseFloat(detailForm.days_worked)||null,basic_salary:parseFloat(detailForm.basic_salary)||0,housing_allowance:parseFloat(detailForm.housing_allowance)||0,transport_allowance:parseFloat(detailForm.transport_allowance)||0,food_allowance:parseFloat(detailForm.food_allowance)||0,other_allowance:parseFloat(detailForm.other_allowance)||0,total_allowances:totalAllowances,overtime:parseFloat(detailForm.overtime)||0,deductions:parseFloat(detailForm.deductions)||0,total_salary:total,notes:detailForm.notes,status:detailForm.status,cost_type:detailForm.cost_type}
    const {error}=editDetail?await supabase.from('payroll_entries').update(payload).eq('id',editDetail.id):await supabase.from('payroll_entries').insert(payload)
    if(error) return showToast(error.message,'error')
    setShowDetailModal(false); showToast(editDetail?'Updated':'Added'); fetchDetail()
  }
  async function deleteQuick(id) { if(!window.confirm('Delete?')) return; await supabase.from('payroll_entries').delete().eq('id',id); fetchQuick() }
  async function deleteDetail(id) { if(!window.confirm('Delete?')) return; await supabase.from('payroll_entries').delete().eq('id',id); fetchDetail() }
  async function approveQuick(id) { await supabase.from('payroll_entries').update({status:'approved'}).eq('id',id); fetchQuick() }
  async function approveDetail(id) { await supabase.from('payroll_entries').update({status:'approved'}).eq('id',id); fetchDetail() }
  async function approveGroup(ids) { await Promise.all(ids.map(id=>supabase.from('payroll_entries').update({status:'approved'}).eq('id',id))); fetchQuick() }

  function toggleQuick(id) { setSelectedQuick(s=>{const n=new Set(s);n.has(id)?n.delete(id):n.add(id);return n}) }
  function toggleAllQuick() { setSelectedQuick(s=>s.size===quickEntries.length?new Set():new Set(quickEntries.map(e=>e.id))) }
  function toggleDetail(id) { setSelectedDetail(s=>{const n=new Set(s);n.has(id)?n.delete(id):n.add(id);return n}) }
  function toggleAllDetail() { setSelectedDetail(s=>s.size===detailEntries.length?new Set():new Set(detailEntries.map(e=>e.id))) }
  async function deleteSelectedQuick() { if(!selectedQuick.size||!window.confirm(`Delete ${selectedQuick.size}?`)) return; await supabase.from('payroll_entries').delete().in('id',[...selectedQuick]); setSelectedQuick(new Set()); fetchQuick() }
  async function deleteSelectedDetail() { if(!selectedDetail.size||!window.confirm(`Delete ${selectedDetail.size}?`)) return; await supabase.from('payroll_entries').delete().in('id',[...selectedDetail]); setSelectedDetail(new Set()); fetchDetail() }

  function downloadQuickTemplate() { const ws=XLSX.utils.json_to_sheet([{sub_code:'Labors.payroll',total_salary:50000,period_type:'monthly',year:2026,month:6,cost_type:'indirect',notes:''}]); const wb=XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb,ws,'QuickPayroll'); XLSX.writeFile(wb,'quick_payroll_template.xlsx') }
  function downloadDetailTemplate() { const ws=XLSX.utils.json_to_sheet([{sub_code:'Labors.payroll',employee_name:'Ahmed',job_title:'Engineer',employee_type:'monthly',period_type:'monthly',year:2026,month:6,days_worked:'',basic_salary:10000,housing_allowance:2000,transport_allowance:500,food_allowance:300,other_allowance:0,overtime:0,deductions:0,cost_type:'direct',notes:''}]); const wb=XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb,ws,'Payroll'); XLSX.writeFile(wb,'payroll_template.xlsx') }
  function exportDetail() { const rows=detailEntries.map(e=>{const sc=subCodesList.find(s=>s.id===e.sub_code_id); return {'Cost Code':sc?.costCode||'—','Resource Code':sc?.resourceCode||'—','Resource Description':sc?.resourceCodeDescription||'','Sub Code':e.cost_codes?.code,'Employee':e.employee_name,'Job Title':e.job_title,'Type':e.employee_type,'D/I':e.cost_type,'Period':periodLabel(e),'Days':e.days_worked||'','Basic':e.basic_salary,'Allowances':e.total_allowances,'OT':e.overtime,'Deductions':e.deductions,'Total':e.total_salary,'Status':e.status}}); const ws=XLSX.utils.json_to_sheet(rows); const wb=XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb,ws,'Payroll'); XLSX.writeFile(wb,'payroll_export.xlsx') }

  function handleQuickFile(e) { const file=e.target.files[0]; if(!file) return; const reader=new FileReader(); reader.onload=ev=>{ try{ const wb=XLSX.read(ev.target.result,{type:'binary'}); const ws=wb.Sheets[wb.SheetNames[0]]; const raw=XLSX.utils.sheet_to_json(ws,{header:1}); if(raw.length<2){showToast('File is empty','error');return}; const headers=raw[0].map(h=>String(h).trim()); const rows=raw.slice(1).filter(r=>r.some(v=>v!==null&&v!==undefined&&v!=='')).map(r=>{const obj={};headers.forEach((h,i)=>{obj[h]=r[i]});return obj}); quickUploadRef.current=rows; setQuickUploadRows([...rows]) }catch{showToast('Error reading file','error')} }; reader.readAsBinaryString(file) }
  function handleDetailFile(e) { const file=e.target.files[0]; if(!file) return; const reader=new FileReader(); reader.onload=ev=>{ try{ const wb=XLSX.read(ev.target.result,{type:'binary'}); const rows=XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]); detailUploadRef.current=rows; setDetailUploadRows([...rows]) }catch{showToast('Error reading file','error')} }; reader.readAsBinaryString(file) }
  async function confirmQuickUpload(rows) {
    const {data:codes}=await supabase.from('cost_codes').select('id,code,level').eq('project_id',projectId).eq('is_active',true)
    const subOnly = (codes||[]).filter(c=>parseInt(c.level)===2)
    let s=0,f=0,levelMismatch=0
    for(const row of rows){
      const rowCode = String(row.sub_code||row.cost_code||'').trim().toLowerCase()   // the template/instructions use sub_code; cost_code stays accepted for older files
      const cc=subOnly.find(c=>c.code.toLowerCase().trim()===rowCode)
      if(!cc){
        const anyMatch=(codes||[]).find(c=>c.code.toLowerCase().trim()===rowCode)
        if (anyMatch) levelMismatch++
        f++;continue
      }
      const {error}=await supabase.from('payroll_entries').insert({project_id:projectId,entry_type:'quick',sub_code_id:cc.id,total_salary:parseFloat(String(row.total_salary).replace(/,/g,''))||0,period_type:String(row.period_type||'monthly'),year:parseInt(row.year)||new Date().getFullYear(),month:String(row.period_type||'monthly')==='yearly'?null:parseInt(row.month)||null,notes:String(row.notes||''),status:'draft',cost_type:String(row.cost_type||'direct')})
      error?f++:s++
    }
    showToast(`Uploaded ${s}${f>0?` — ${f} failed${levelMismatch>0?` (${levelMismatch} كود مش Sub Code فعلي)`:''}`:''}`)
    quickUploadRef.current=[]; setQuickUploadRows([]); setShowQuickUpload(false); if(quickFileRef.current) quickFileRef.current.value=''; fetchQuick()
  }
  async function confirmDetailUpload() {
    const rows=detailUploadRef.current
    const {data:codes}=await supabase.from('cost_codes').select('id,code,level').eq('project_id',projectId).eq('is_active',true)
    const subOnly = (codes||[]).filter(c=>parseInt(c.level)===2)
    let s=0,f=0,levelMismatch=0
    for(const row of rows){
      const rowCode = String(row.sub_code||row.cost_code||'').trim().toLowerCase()   // the template/instructions use sub_code; cost_code stays accepted for older files
      const cc=subOnly.find(c=>c.code.toLowerCase().trim()===rowCode)
      if(!cc||!String(row.employee_name||'').trim()){
        const anyMatch=(codes||[]).find(c=>c.code.toLowerCase().trim()===rowCode)
        if (cc===undefined && anyMatch) levelMismatch++
        f++;continue
      }
      const basic=parseFloat(row.basic_salary)||0;const days=parseFloat(row.days_worked)||0;const empType=row.employee_type||'monthly';const dailyBasic=empType==='daily'?basic*days:basic;const housing=parseFloat(row.housing_allowance)||0;const transport=parseFloat(row.transport_allowance)||0;const food=parseFloat(row.food_allowance)||0;const other=parseFloat(row.other_allowance)||0;const overtime=parseFloat(row.overtime)||0;const deductions=parseFloat(row.deductions)||0;const total=dailyBasic+housing+transport+food+other+overtime-deductions
      const {error}=await supabase.from('payroll_entries').insert({project_id:projectId,entry_type:'detailed',sub_code_id:cc.id,employee_name:String(row.employee_name).trim(),job_title:String(row.job_title||'').trim(),employee_type:empType,period_type:row.period_type||'monthly',year:parseInt(row.year)||new Date().getFullYear(),month:(row.period_type||'monthly')==='yearly'?null:parseInt(row.month)||null,days_worked:days||null,basic_salary:basic,housing_allowance:housing,transport_allowance:transport,food_allowance:food,other_allowance:other,total_allowances:housing+transport+food+other,overtime,deductions,total_salary:total,notes:String(row.notes||''),status:'draft',cost_type:String(row.cost_type||'direct')})
      error?f++:s++
    }
    showToast(`Uploaded ${s}${f>0?` — ${f} failed${levelMismatch>0?` (${levelMismatch} كود مش Sub Code فعلي)`:''}`:''}`)
    detailUploadRef.current=[]; setDetailUploadRows([]); setShowDetailUpload(false); if(detailFileRef.current) detailFileRef.current.value=''; fetchDetail()
  }

  const totalApprovedQuick  = quickEntries.filter(e=>e.status==='approved').reduce((s,e)=>s+(parseFloat(e.total_salary)||0),0)
  const totalApprovedDetail = detailEntries.filter(e=>e.status==='approved').reduce((s,e)=>s+(parseFloat(e.total_salary)||0),0)
  const allApproved = [...quickEntries,...detailEntries].filter(e=>e.status==='approved')
  const totalByCode = kw => allApproved.filter(e=>{const sc=subCodesList.find(c=>c.id===e.sub_code_id);return sc&&sc.code.toLowerCase().includes(kw.toLowerCase())}).reduce((s,e)=>s+(parseFloat(e.total_salary)||0),0)
  const totalStaff=totalByCode('staff'), totalLabor=totalByCode('labor'), totalFormans=totalByCode('forman')
  const totalAll=totalStaff+totalLabor+totalFormans
  const allEntries2=[...quickEntries,...detailEntries]
  const latestEntry=allEntries2.length>0?allEntries2.reduce((l,e)=>(parseInt(e.year)*100+(parseInt(e.month)||0))>(parseInt(l.year)*100+(parseInt(l.month)||0))?e:l,allEntries2[0]):null
  const latestPeriodLabel=latestEntry?(latestEntry.period_type==='yearly'?String(latestEntry.year):`${MONTHS[(parseInt(latestEntry.month)||1)-1]}-${latestEntry.year}`):'—'

  // ── Project Select ──
  if(!projectId) return (
    <div style={{padding:32,maxWidth:800,margin:'0 auto',fontFamily:'inherit'}}>
      <div style={{fontSize:F.heading,fontWeight:800,color:C.ink,marginBottom:6}}>HR / Payroll</div>
      <div style={{fontSize:F.body,color:C.sub,marginBottom:20}}>Select a project</div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:12}}>
        {projects.map(p=>(
          <button key={p.id} onClick={()=>{setProjectId(p.id);setProjectName(p.name)}}
            style={{textAlign:'left',background:C.surface,border:`1px solid ${C.border}`,borderRadius:R.card,padding:16,cursor:'pointer',fontFamily:'inherit',transition:'all .12s'}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor=C.blue;e.currentTarget.style.boxShadow=`0 2px 8px ${C.blueBg}`}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.boxShadow='none'}}>
            <div style={{fontWeight:700,color:C.ink,fontSize:F.title}}>{p.name}</div>
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <div style={{minHeight:'100vh',background:C.pageBg,fontFamily:'inherit'}}>

      {/* Header */}
      <div style={{...pageHeader,padding:'14px 24px'}}>
        <div>
          <div style={{fontSize:F.heading,fontWeight:800,color:C.ink,letterSpacing:'-.02em'}}>HR / Payroll</div>
          <div style={{fontSize:F.caption,color:C.sub,marginTop:2}}>{projectName}</div>
        </div>
        {latestEntry && (
          <div style={{display:'flex',alignItems:'center',gap:6,background:C.blueBg,border:`1px solid ${C.blueLine}`,borderRadius:R.input,padding:'4px 12px'}}>
            <span style={{fontSize:F.label,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:C.blue}}>Actual Cost Date</span>
            <span style={{fontSize:F.body,fontWeight:800,color:C.blue}}>{latestPeriodLabel}</span>
          </div>
        )}
        <div style={{flex:1}}/>
        <button onClick={()=>{setProjectId(null)}} style={{...btnGhost,color:C.sub,fontSize:F.caption}}>← Projects</button>
        <div style={{display:'flex',gap:8}}>
          {activeTab==='quick' && <>
            <button onClick={downloadQuickTemplate} style={{...btnSecondary,fontSize:F.caption,padding:'6px 12px'}}>📥 Template</button>
            <button onClick={()=>setShowQuickUpload(v=>!v)} style={{...btnSecondary,fontSize:F.caption,padding:'6px 12px'}}>↑ Upload</button>
            {selectedQuick.size>0 && <button onClick={deleteSelectedQuick} style={{...btnDanger,fontSize:F.caption,padding:'6px 12px'}}>Delete {selectedQuick.size}</button>}
            <button onClick={()=>{setEditQuick(null);setQuickForm(EMPTY_QUICK);setShowQuickModal(true)}} style={{...btnPrimary,fontSize:F.caption,padding:'6px 14px'}}>+ Quick Add</button>
          </>}
          {activeTab==='detailed' && <>
            <button onClick={downloadDetailTemplate} style={{...btnSecondary,fontSize:F.caption,padding:'6px 12px'}}>📥 Template</button>
            <button onClick={()=>setShowDetailUpload(v=>!v)} style={{...btnSecondary,fontSize:F.caption,padding:'6px 12px'}}>↑ Upload</button>
            <button onClick={exportDetail} style={{...btnSecondary,fontSize:F.caption,padding:'6px 12px'}}>↓ Export</button>
            {selectedDetail.size>0 && <button onClick={deleteSelectedDetail} style={{...btnDanger,fontSize:F.caption,padding:'6px 12px'}}>Delete {selectedDetail.size}</button>}
            <button onClick={()=>{setEditDetail(null);setDetailForm(EMPTY_DETAIL);setShowDetailModal(true)}} style={{...btnPrimary,fontSize:F.caption,padding:'6px 14px'}}>+ Add Entry</button>
          </>}
        </div>
      </div>

      <div style={{padding:'20px 24px',display:'flex',flexDirection:'column',gap:16}}>

        {/* KPI Cards */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
          {[
            {label:'Staff',     value:totalStaff,   color:C.blue,   bg:C.blueBg,   border:C.blueLine},
            {label:'Labour',    value:totalLabor,   color:C.green,  bg:C.greenBg,  border:C.greenLine},
            {label:'Formans',   value:totalFormans, color:C.amber,  bg:'#FEF3C7',  border:'#FDE68A'},
            {label:'Total Salaries', value:totalAll, color:C.ink,   bg:C.pageBg,   border:C.border},
          ].map(s=>(
            <div key={s.label} style={{background:s.bg,border:`1px solid ${s.border}`,borderRadius:R.card,padding:'14px 18px'}}>
              <div style={{fontSize:F.label,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:C.muted,marginBottom:6}}>{s.label}</div>
              <div style={{fontSize:20,fontWeight:800,color:s.color,fontVariantNumeric:'tabular-nums'}}>SAR {fmt(s.value)}</div>
              <div style={{fontSize:F.caption,color:C.muted,marginTop:3}}>Approved only</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,display:'flex',borderRadius:0,margin:'0 -0px'}}>
          {[{key:'quick',label:'⚡ Quick Entry'},{key:'detailed',label:'📋 Detailed'}].map(t=>(
            <button key={t.key} onClick={()=>setActiveTab(t.key)}
              style={{padding:'9px 20px',fontSize:F.bodyLg,fontWeight:activeTab===t.key?700:500,color:activeTab===t.key?C.ink:C.sub,background:'transparent',border:'none',borderBottom:`2px solid ${activeTab===t.key?C.blue:'transparent'}`,cursor:'pointer',fontFamily:'inherit',transition:'all .15s'}}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── QUICK TAB ── */}
        {activeTab==='quick' && <>
          {showQuickUpload && (
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:R.card,padding:16}}>
              <div style={{fontWeight:600,marginBottom:6,color:C.ink,fontSize:F.body}}>Upload Quick Payroll from Excel</div>
              <div style={{fontSize:F.caption,color:C.sub,marginBottom:10}}>Columns: <strong>sub_code, total_salary</strong>, period_type, year, month, cost_type, notes</div>
              <input ref={quickFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleQuickFile} style={{fontSize:F.body,marginBottom:10,display:'block'}}/>
              {quickUploadRows.length>0 && (
                <div>
                  <div style={{fontSize:F.body,marginBottom:8,color:C.ink}}>Read <strong>{quickUploadRows.length}</strong> rows</div>
                  <button onClick={()=>confirmQuickUpload(quickUploadRows)} style={{...btnPrimary,fontSize:F.caption}}>✓ Confirm Upload ({quickUploadRows.length} rows)</button>
                </div>
              )}
            </div>
          )}

          {quickLoading ? (
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,padding:'60px 0'}}>
              <div style={{...spinner,borderTopColor:C.blue}}/><span style={{fontSize:F.body,color:C.sub}}>Loading...</span>
            </div>
          ) : quickEntries.length===0 ? (
            <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:R.card,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:32,marginBottom:8}}>⚡</div>
              <div style={{color:C.sub}}>No entries yet. Click "+ Quick Add" or upload Excel.</div>
            </div>
          ) : (
            <QuickGroupedTable entries={quickEntries} selectedQuick={selectedQuick} toggleQuick={toggleQuick} toggleAllQuick={toggleAllQuick}
              subCodesList={subCodesList}
              approveGroup={approveGroup} setEditQuick={setEditQuick} setQuickForm={setQuickForm} setShowQuickModal={setShowQuickModal}
              deleteQuick={deleteQuick} totalApprovedQuick={totalApprovedQuick} fmt={fmt}/>
          )}
        </>}

        {/* ── DETAILED TAB ── */}
        {activeTab==='detailed' && <>
          {showDetailUpload && (
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:R.card,padding:16}}>
              <div style={{fontWeight:600,marginBottom:6,color:C.ink,fontSize:F.body}}>Upload Detailed Payroll from Excel</div>
              <div style={{fontSize:F.caption,color:C.sub,marginBottom:10}}>Required: <strong>sub_code, employee_name</strong></div>
              <input ref={detailFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleDetailFile} style={{fontSize:F.body,marginBottom:10,display:'block'}}/>
              {detailUploadRows.length>0 && (
                <div>
                  <div style={{fontSize:F.body,marginBottom:8}}>Read <strong>{detailUploadRows.length}</strong> rows</div>
                  <button onClick={confirmDetailUpload} style={{...btnPrimary,fontSize:F.caption}}>✓ Confirm Upload ({detailUploadRows.length} rows)</button>
                </div>
              )}
            </div>
          )}

          {detailLoading ? (
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,padding:'60px 0'}}>
              <div style={{...spinner,borderTopColor:C.blue}}/><span style={{fontSize:F.body,color:C.sub}}>Loading...</span>
            </div>
          ) : detailEntries.length===0 ? (
            <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:R.card,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:32,marginBottom:8}}>📋</div>
              <div style={{color:C.sub}}>No entries yet. Click "+ Add Entry" or upload Excel.</div>
            </div>
          ) : (
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:R.card,overflow:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:F.body}}>
                <thead>
                  <tr>
                    <th style={{...thStyle,width:36,padding:'9px 10px'}}><input type="checkbox" checked={selectedDetail.size===detailEntries.length&&detailEntries.length>0} onChange={toggleAllDetail}/></th>
                    {['Cost Code','Resource Code','Resource Description','Sub Code','Employee','D/I','Type','Period','Basic','Allow','OT','Deduct','Total','Status',''].map(h=>(
                      <th key={h} style={{...thStyle,textAlign:['Basic','Allow','OT','Deduct','Total'].includes(h)?'right':'left'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detailEntries.map((e,i)=>{
                    const sc = subCodesList.find(s=>s.id===e.sub_code_id)
                    return (
                    <tr key={e.id} style={{borderBottom:`1px solid ${C.border}`,background:selectedDetail.has(e.id)?C.blueBg:'transparent'}}>
                      <td style={{padding:'8px 10px'}}><input type="checkbox" checked={selectedDetail.has(e.id)} onChange={()=>toggleDetail(e.id)}/></td>
                      <td style={{padding:'8px 10px',fontFamily:'monospace',fontSize:F.caption,color:C.sub}}>{sc?.costCode||'—'}</td>
                      <td style={{padding:'8px 10px',fontFamily:'monospace',fontSize:F.caption,color:'#7C3AED'}}>{sc?.resourceCode||'—'}</td>
                      <td style={{padding:'8px 10px',fontSize:F.caption,color:'#7C3AED'}}>{sc?.resourceCodeDescription||'—'}</td>
                      <td style={{padding:'8px 10px',fontFamily:'monospace',fontSize:F.caption,color:C.green,fontWeight:600}}>{e.cost_codes?.code}</td>
                      <td style={{padding:'8px 10px'}}>
                        <div style={{fontWeight:600,color:C.ink}}>{e.employee_name}</div>
                        <div style={{fontSize:F.caption,color:C.muted}}>{e.job_title}</div>
                      </td>
                      <td style={{padding:'8px 10px'}}>
                        <span style={{padding:'2px 6px',borderRadius:R.badge,fontSize:F.caption,fontWeight:600,background:e.cost_type==='indirect'?'#FEF3C7':'#DBEAFE',color:e.cost_type==='indirect'?'#92400E':'#1E40AF'}}>
                          {e.cost_type==='indirect'?'IND':'DIR'}
                        </span>
                      </td>
                      <td style={{padding:'8px 10px',fontSize:F.caption,color:C.sub}}>{e.employee_type}{e.days_worked?` (${e.days_worked}d)`:''}</td>
                      <td style={{padding:'8px 10px',color:C.sub,fontSize:F.caption}}>{periodLabel(e)}</td>
                      <td style={{padding:'8px 10px',textAlign:'right',fontVariantNumeric:'tabular-nums'}}>{fmt(e.basic_salary)}</td>
                      <td style={{padding:'8px 10px',textAlign:'right',color:C.green,fontVariantNumeric:'tabular-nums'}}>{fmt(e.total_allowances)}</td>
                      <td style={{padding:'8px 10px',textAlign:'right',color:C.green,fontVariantNumeric:'tabular-nums'}}>{fmt(e.overtime)}</td>
                      <td style={{padding:'8px 10px',textAlign:'right',color:C.red,fontVariantNumeric:'tabular-nums'}}>{fmt(e.deductions)}</td>
                      <td style={{padding:'8px 10px',textAlign:'right',fontWeight:700,background:C.pageBg,fontVariantNumeric:'tabular-nums'}}>{fmt(e.total_salary)}</td>
                      <td style={{padding:'8px 10px'}}><StatusPill status={e.status}/></td>
                      <td style={{padding:'8px 10px'}}>
                        <div style={{display:'flex',gap:4,justifyContent:'flex-end'}}>
                          {e.status==='draft' && <button onClick={()=>approveDetail(e.id)} style={{...btnGhost,fontSize:F.caption,color:C.green,padding:'2px 8px'}}>✓</button>}
                          <button onClick={()=>{setEditDetail(e);setDetailForm({sub_code_id:e.sub_code_id,employee_name:e.employee_name||'',job_title:e.job_title||'',employee_type:e.employee_type||'monthly',period_type:e.period_type||'monthly',year:e.year,month:e.month||1,days_worked:e.days_worked||'',basic_salary:e.basic_salary||'',housing_allowance:e.housing_allowance||'',transport_allowance:e.transport_allowance||'',food_allowance:e.food_allowance||'',other_allowance:e.other_allowance||'',overtime:e.overtime||'',deductions:e.deductions||'',notes:e.notes||'',status:e.status,cost_type:e.cost_type||'direct'});setShowDetailModal(true)}} style={{...btnGhost,fontSize:F.caption,color:C.sub,padding:'2px 8px'}}>✏</button>
                          <button onClick={()=>deleteDetail(e.id)} style={{...btnGhost,fontSize:F.caption,color:C.red,padding:'2px 8px'}}>🗑</button>
                        </div>
                      </td>
                    </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr style={{borderTop:`2px solid ${C.border2}`,background:C.pageBg}}>
                    <td colSpan={12} style={{padding:'9px 10px',fontWeight:600,color:C.sub,fontSize:F.body}}>Total Approved</td>
                    <td style={{padding:'9px 10px',textAlign:'right',fontWeight:800,color:C.ink,fontSize:F.body,fontVariantNumeric:'tabular-nums'}}>{fmt(totalApprovedDetail)}</td>
                    <td colSpan={2}/>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>}
      </div>

      {/* ── Quick Modal ── */}
      {showQuickModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:200}}>
          <div style={{background:C.surface,borderRadius:R.card+4,width:'100%',maxWidth:420,margin:16,boxShadow:'0 20px 60px rgba(0,0,0,.2)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'16px 20px',borderBottom:`1px solid ${C.border}`}}>
              <div style={{fontWeight:700,fontSize:F.title,color:C.ink}}>⚡ {editQuick?'Edit':'Quick Add'}</div>
              <button onClick={()=>setShowQuickModal(false)} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:C.muted}}>×</button>
            </div>
            <div style={{padding:20,display:'flex',flexDirection:'column',gap:12}}>
              <Field label="Sub Code *">
                <select value={quickForm.sub_code_id} onChange={e=>setQuickForm(p=>({...p,sub_code_id:e.target.value}))} style={{...input}}>
                  <option value="">— Select —</option>
                  {subCodesList.map(s=><option key={s.id} value={s.id}>{s.code} — {s.description}</option>)}
                </select>
                {quickForm.sub_code_id && (()=>{ const sel=subCodesList.find(s=>s.id===quickForm.sub_code_id); if(!sel) return null; return (
                  <div style={{marginTop:6}}>
                    <div style={{fontSize:F.caption,color:C.sub}}>Cost Code: <span style={{fontFamily:'monospace',fontWeight:700,color:C.green}}>{sel.costCode}</span> — {sel.costCodeDescription}</div>
                    {sel.resourceCode && <div style={{fontSize:F.caption,color:'#7C3AED'}}>Resource Code: <span style={{fontFamily:'monospace',fontWeight:700}}>{sel.resourceCode}</span> — {sel.resourceCodeDescription}</div>}
                  </div>
                )})()}
              </Field>
              <Field label="Total Salary (SAR) *">
                <input type="number" value={quickForm.total_salary} onChange={e=>setQuickForm(p=>({...p,total_salary:e.target.value}))} style={{...input}}/>
              </Field>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <Field label="Cost Type">
                  <select value={quickForm.cost_type} onChange={e=>setQuickForm(p=>({...p,cost_type:e.target.value}))} style={{...input}}>
                    <option value="direct">Direct</option><option value="indirect">Indirect</option>
                  </select>
                </Field>
                <Field label="Period">
                  <select value={quickForm.period_type} onChange={e=>setQuickForm(p=>({...p,period_type:e.target.value}))} style={{...input}}>
                    <option value="monthly">Monthly</option><option value="yearly">Yearly</option>
                  </select>
                </Field>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <Field label="Year">
                  <select value={quickForm.year} onChange={e=>setQuickForm(p=>({...p,year:e.target.value}))} style={{...input}}>
                    {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
                  </select>
                </Field>
                {quickForm.period_type==='monthly' && (
                  <Field label="Month">
                    <select value={quickForm.month} onChange={e=>setQuickForm(p=>({...p,month:e.target.value}))} style={{...input}}>
                      {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
                    </select>
                  </Field>
                )}
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <Field label="Status">
                  <select value={quickForm.status} onChange={e=>setQuickForm(p=>({...p,status:e.target.value}))} style={{...input}}>
                    <option value="draft">Draft</option><option value="approved">Approved</option>
                  </select>
                </Field>
                <Field label="Notes">
                  <input value={quickForm.notes} onChange={e=>setQuickForm(p=>({...p,notes:e.target.value}))} style={{...input}}/>
                </Field>
              </div>
            </div>
            <div style={{display:'flex',gap:8,padding:'12px 20px',borderTop:`1px solid ${C.border}`}}>
              <button onClick={()=>setShowQuickModal(false)} style={{...btnSecondary,flex:1,justifyContent:'center'}}>Cancel</button>
              <button onClick={saveQuick} style={{...btnPrimary,flex:1,justifyContent:'center'}}>{editQuick?'Update':'Add'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Detail Modal ── */}
      {showDetailModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:200}}>
          <div style={{background:C.surface,borderRadius:R.card+4,width:'100%',maxWidth:640,margin:16,maxHeight:'90vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,.2)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'16px 20px',borderBottom:`1px solid ${C.border}`}}>
              <div style={{fontWeight:700,fontSize:F.title,color:C.ink}}>📋 {editDetail?'Edit Entry':'New Entry'}</div>
              <button onClick={()=>setShowDetailModal(false)} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:C.muted}}>×</button>
            </div>
            <div style={{padding:20,display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div style={{gridColumn:'1/-1'}}>
                <Field label="Sub Code *">
                  <select value={detailForm.sub_code_id} onChange={e=>setDetailForm(p=>({...p,sub_code_id:e.target.value}))} style={{...input}}>
                    <option value="">— Select —</option>
                    {subCodesList.map(s=><option key={s.id} value={s.id}>{s.code} — {s.description}</option>)}
                  </select>
                  {detailForm.sub_code_id && (()=>{ const sel=subCodesList.find(s=>s.id===detailForm.sub_code_id); if(!sel) return null; return (
                    <div style={{marginTop:6}}>
                      <div style={{fontSize:F.caption,color:C.sub}}>Cost Code: <span style={{fontFamily:'monospace',fontWeight:700,color:C.green}}>{sel.costCode}</span> — {sel.costCodeDescription}</div>
                      {sel.resourceCode && <div style={{fontSize:F.caption,color:'#7C3AED'}}>Resource Code: <span style={{fontFamily:'monospace',fontWeight:700}}>{sel.resourceCode}</span> — {sel.resourceCodeDescription}</div>}
                    </div>
                  )})()}
                </Field>
              </div>
              <Field label="Employee Name *"><input value={detailForm.employee_name} onChange={e=>setDetailForm(p=>({...p,employee_name:e.target.value}))} style={{...input}}/></Field>
              <Field label="Job Title"><input value={detailForm.job_title} onChange={e=>setDetailForm(p=>({...p,job_title:e.target.value}))} style={{...input}}/></Field>
              <Field label="Cost Type">
                <select value={detailForm.cost_type} onChange={e=>setDetailForm(p=>({...p,cost_type:e.target.value}))} style={{...input}}>
                  <option value="direct">Direct</option><option value="indirect">Indirect</option>
                </select>
              </Field>
              <Field label="Employee Type">
                <select value={detailForm.employee_type} onChange={e=>setDetailForm(p=>({...p,employee_type:e.target.value}))} style={{...input}}>
                  <option value="monthly">Monthly</option><option value="daily">Daily Rate</option>
                </select>
              </Field>
              <Field label="Period">
                <select value={detailForm.period_type} onChange={e=>setDetailForm(p=>({...p,period_type:e.target.value}))} style={{...input}}>
                  <option value="monthly">Monthly</option><option value="yearly">Yearly</option>
                </select>
              </Field>
              <Field label="Year">
                <select value={detailForm.year} onChange={e=>setDetailForm(p=>({...p,year:e.target.value}))} style={{...input}}>
                  {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
                </select>
              </Field>
              {detailForm.period_type==='monthly' && (
                <Field label="Month">
                  <select value={detailForm.month} onChange={e=>setDetailForm(p=>({...p,month:e.target.value}))} style={{...input}}>
                    {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
                  </select>
                </Field>
              )}
              {detailForm.employee_type==='daily' && (
                <Field label="Days Worked"><input type="number" value={detailForm.days_worked} onChange={e=>setDetailForm(p=>({...p,days_worked:e.target.value}))} style={{...input}}/></Field>
              )}
              <div style={{gridColumn:'1/-1',borderTop:`1px solid ${C.border}`,paddingTop:12}}>
                <div style={{fontSize:F.caption,fontWeight:600,color:C.sub,marginBottom:8,textTransform:'uppercase',letterSpacing:'.06em'}}>Salary Breakdown</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  {[
                    {label:detailForm.employee_type==='daily'?'Daily Rate':'Basic Salary',key:'basic_salary'},
                    {label:'Housing Allowance',key:'housing_allowance'},
                    {label:'Transport Allowance',key:'transport_allowance'},
                    {label:'Food Allowance',key:'food_allowance'},
                    {label:'Other Allowance',key:'other_allowance'},
                    {label:'Overtime',key:'overtime'},
                    {label:'Deductions',key:'deductions'},
                  ].map(f=>(
                    <Field key={f.key} label={f.label}>
                      <input type="number" value={detailForm[f.key]} onChange={e=>setDetailForm(p=>({...p,[f.key]:e.target.value}))} placeholder="0" style={{...input}}/>
                    </Field>
                  ))}
                </div>
              </div>
              <div style={{gridColumn:'1/-1',background:C.pageBg,borderRadius:R.input,padding:12,display:'flex',justifyContent:'space-between',alignItems:'center',border:`1px solid ${C.border}`}}>
                <span style={{fontWeight:600,color:C.ink}}>Total Salary</span>
                <span style={{fontSize:20,fontWeight:800,color:C.green}}>SAR {fmt(calcTotal(detailForm))}</span>
              </div>
              <Field label="Status">
                <select value={detailForm.status} onChange={e=>setDetailForm(p=>({...p,status:e.target.value}))} style={{...input}}>
                  <option value="draft">Draft</option><option value="approved">Approved</option>
                </select>
              </Field>
              <Field label="Notes"><input value={detailForm.notes} onChange={e=>setDetailForm(p=>({...p,notes:e.target.value}))} style={{...input}}/></Field>
            </div>
            <div style={{display:'flex',gap:8,padding:'12px 20px',borderTop:`1px solid ${C.border}`}}>
              <button onClick={()=>setShowDetailModal(false)} style={{...btnSecondary,flex:1,justifyContent:'center'}}>Cancel</button>
              <button onClick={saveDetail} style={{...btnPrimary,flex:1,justifyContent:'center'}}>{editDetail?'Update':'Add Entry'}</button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast {...toast} onClose={()=>setToast(null)}/>}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}

function QuickGroupedTable({ entries, selectedQuick, toggleQuick, toggleAllQuick, subCodesList, approveGroup, setEditQuick, setQuickForm, setShowQuickModal, deleteQuick, totalApprovedQuick, fmt }) {
  const [expanded, setExpanded] = useState(new Set())
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const groups = {}
  entries.forEach(e => {
    const key = e.period_type==='yearly'?`${e.year}`:`${e.year}-${String(e.month||1).padStart(2,'0')}`
    const label = e.period_type==='yearly'?`${e.year}`:`${MONTHS[(e.month||1)-1]} ${e.year}`
    if (!groups[key]) groups[key]={key,label,entries:[],year:e.year,month:e.month}
    groups[key].entries.push(e)
  })
  const sorted = Object.values(groups).sort((a,b)=>b.key.localeCompare(a.key))
  function toggleExpand(key) { setExpanded(s=>{const n=new Set(s);n.has(key)?n.delete(key):n.add(key);return n}) }

  return (
    <div style={{display:'flex',flexDirection:'column',gap:8}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <input type="checkbox" checked={selectedQuick.size===entries.length&&entries.length>0} onChange={toggleAllQuick}/>
          <span style={{fontSize:F.body,color:C.sub}}>Select All ({entries.length})</span>
          {selectedQuick.size>0 && (
            <button onClick={()=>approveGroup([...selectedQuick])}
              style={{padding:'4px 12px',background:C.greenBg,border:`1px solid ${C.greenLine}`,borderRadius:R.input,fontSize:F.body,fontWeight:600,color:C.green,cursor:'pointer'}}>
              ✓ Approve Selected ({selectedQuick.size})
            </button>
          )}
        </div>
        <div style={{fontSize:F.bodyLg,fontWeight:700,color:C.green}}>Total Approved: SAR {fmt(totalApprovedQuick)}</div>
      </div>

      {sorted.map(group=>{
        const total=group.entries.reduce((s,e)=>s+(parseFloat(e.total_salary)||0),0)
        const approved=group.entries.filter(e=>e.status==='approved').reduce((s,e)=>s+(parseFloat(e.total_salary)||0),0)
        const draftIds=group.entries.filter(e=>e.status==='draft').map(e=>e.id)
        const allApproved=draftIds.length===0
        const isOpen=expanded.has(group.key)
        const groupSelected=group.entries.every(e=>selectedQuick.has(e.id))
        return (
          <div key={group.key} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:R.card,overflow:'hidden'}}>
            <div style={{display:'flex',alignItems:'center',padding:'12px 16px',cursor:'pointer',gap:12,background:allApproved?C.greenBg:C.surface,borderBottom:isOpen?`1px solid ${C.border}`:'none'}}
              onClick={()=>toggleExpand(group.key)}>
              <input type="checkbox" checked={groupSelected} onClick={e=>e.stopPropagation()} onChange={()=>group.entries.forEach(e=>toggleQuick(e.id))}/>
              <span style={{fontSize:F.caption,color:C.sub}}>{isOpen?'▼':'▶'}</span>
              <div style={{flex:1}}>
                <span style={{fontWeight:700,fontSize:F.title,color:C.ink}}>{group.label}</span>
                <span style={{marginLeft:10,fontSize:F.caption,color:C.sub}}>{group.entries.length} entries</span>
              </div>
              <div style={{textAlign:'right',marginRight:16}}>
                <div style={{fontSize:F.caption,color:C.muted}}>Approved</div>
                <div style={{fontWeight:700,color:C.green,fontSize:F.body}}>SAR {fmt(approved)}</div>
              </div>
              <div style={{textAlign:'right',marginRight:16}}>
                <div style={{fontSize:F.caption,color:C.muted}}>Total</div>
                <div style={{fontWeight:700,color:C.ink,fontSize:F.body}}>SAR {fmt(total)}</div>
              </div>
              <div style={{display:'flex',gap:6}} onClick={e=>e.stopPropagation()}>
                {!allApproved && (
                  <button onClick={()=>approveGroup(draftIds)}
                    style={{padding:'4px 10px',background:C.greenBg,border:`1px solid ${C.greenLine}`,borderRadius:R.input,fontSize:F.caption,fontWeight:600,color:C.green,cursor:'pointer'}}>
                    ✓ Approve All
                  </button>
                )}
                {allApproved && <span style={{fontSize:F.caption,color:C.green,fontWeight:600}}>✓ All Approved</span>}
              </div>
            </div>
            {isOpen && (
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:F.body}}>
                <thead>
                  <tr style={{background:C.pageBg}}>
                    <th style={{width:36,padding:'6px 10px'}}/>
                    {['Cost Code','Resource Code','Resource Description','Sub Code','D/I','Total Salary','Status',''].map(h=>(
                      <th key={h} style={{padding:'6px 10px',textAlign:h==='Total Salary'?'right':'left',color:C.sub,fontWeight:600,fontSize:F.caption}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {group.entries.map(e=>{
                    const sc = (subCodesList||[]).find(s=>s.id===e.sub_code_id)
                    return (
                    <tr key={e.id} style={{borderTop:`1px solid ${C.border}`,background:selectedQuick.has(e.id)?C.blueBg:'transparent'}}>
                      <td style={{padding:'8px 10px'}}><input type="checkbox" checked={selectedQuick.has(e.id)} onChange={()=>toggleQuick(e.id)}/></td>
                      <td style={{padding:'8px 10px',fontFamily:'monospace',fontSize:F.caption,color:C.sub}}>{sc?.costCode||'—'}</td>
                      <td style={{padding:'8px 10px',fontFamily:'monospace',fontSize:F.caption,color:'#7C3AED'}}>{sc?.resourceCode||'—'}</td>
                      <td style={{padding:'8px 10px',fontSize:F.caption,color:'#7C3AED'}}>{sc?.resourceCodeDescription||'—'}</td>
                      <td style={{padding:'8px 10px',fontFamily:'monospace',fontSize:F.caption,color:C.green,fontWeight:600}}>{e.cost_codes?.code}</td>
                      <td style={{padding:'8px 10px'}}>
                        <span style={{padding:'2px 6px',borderRadius:R.badge,fontSize:F.caption,fontWeight:600,background:e.cost_type==='indirect'?'#FEF3C7':'#DBEAFE',color:e.cost_type==='indirect'?'#92400E':'#1E40AF'}}>
                          {e.cost_type==='indirect'?'IND':'DIR'}
                        </span>
                      </td>
                      <td style={{padding:'8px 10px',textAlign:'right',fontWeight:700,fontVariantNumeric:'tabular-nums'}}>SAR {fmt(e.total_salary)}</td>
                      <td style={{padding:'8px 10px'}}>
                        <span style={{padding:'2px 6px',borderRadius:R.badge,fontSize:F.caption,fontWeight:600,background:e.status==='approved'?C.greenBg:C.pageBg,color:e.status==='approved'?C.green:C.sub}}>
                          {e.status}
                        </span>
                      </td>
                      <td style={{padding:'8px 10px'}}>
                        <div style={{display:'flex',gap:4,justifyContent:'flex-end'}}>
                          <button onClick={()=>{setEditQuick(e);setQuickForm({sub_code_id:e.sub_code_id,total_salary:e.total_salary,period_type:e.period_type,year:e.year,month:e.month||1,notes:e.notes||'',status:e.status,cost_type:e.cost_type||'direct'});setShowQuickModal(true)}}
                            style={{background:'none',border:'none',color:C.sub,cursor:'pointer',fontSize:F.body,padding:'2px 6px'}}>✏</button>
                          <button onClick={()=>deleteQuick(e.id)} style={{background:'none',border:'none',color:C.red,cursor:'pointer',fontSize:F.body,padding:'2px 6px'}}>🗑</button>
                        </div>
                      </td>
                    </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        )
      })}
    </div>
  )
}
